/**
 * Exact surface-phrase evidence for ATS-style match UI.
 *
 * A job concept counts as exact-matched when:
 *   - the same surface string appears in both JD and resume, OR
 *   - each side has a valid related surface for that concept
 *     (e.g. JD "presentation materials" + resume "presentation skills").
 *
 * Rejects cross-concept pollution (Financial modeling ≠ Financial analysis)
 * and stale aliases that drop required distinctive tokens
 * (investment analysis ≠ Financial analysis).
 * Client-safe: no Node fs; dictionary aliases come from ats_keyword_surfaces.v1.json.
 */

import { dictionarySurfacesFor } from "./dictionarySurfaces.js";
import { normalizeMatchText, normalizePattern, textHasKeywordPhrase } from "./normalizeText.js";

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripHtml(value) {
  return String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|li|h[1-6]|ul|ol|blockquote)\s*>/gi, "\n")
    // Inline tags must not insert spaces (Account<strong>ing</strong> → Accounting).
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Flatten resume profile into searchable plain text.
 * @param {object | null | undefined} profile
 */
export function flattenResumeProfileText(profile) {
  if (!profile || typeof profile !== "object") return "";
  const chunks = [];

  const push = (value) => {
    const text = String(value || "").trim();
    if (text) chunks.push(text);
  };

  const basics = profile.basics || {};
  push(basics.full_name);
  push(basics.headline);
  push(basics.location);
  push(profile.summary);

  for (const item of profile.experience || []) {
    push(item.title);
    push(item.company);
    push(item.location);
    for (const bullet of item.bullets || []) push(bullet);
  }
  for (const item of profile.education || []) {
    push(item.school);
    push(item.degree);
    push(item.field);
    push(item.honors);
    for (const course of item.coursework || []) push(course);
  }
  for (const item of profile.projects || []) {
    push(item.name);
    for (const tech of item.tech || []) push(tech);
    for (const bullet of item.bullets || []) push(bullet);
  }
  for (const skill of profile.skills || []) {
    push(typeof skill === "string" ? skill : skill?.name);
  }
  for (const course of profile.coursework || []) push(course);
  for (const item of profile.certifications || []) {
    push(item.name);
    push(item.issuer);
  }
  for (const item of profile.languages || []) push(item.name);
  for (const item of profile.activities || []) {
    push(item.name);
    push(item.role);
    push(item.organization);
    for (const bullet of item.bullets || []) push(bullet);
  }
  for (const item of profile.awards || []) {
    push(item.name);
    push(item.description);
  }
  for (const item of profile.volunteer || []) {
    push(item.role);
    push(item.organization);
    for (const bullet of item.bullets || []) push(bullet);
  }
  for (const item of profile.publications || []) {
    push(item.title);
    push(item.publisher);
  }
  for (const interest of profile.interests || []) push(interest);

  return chunks.join("\n");
}

export function prepareJobText(description) {
  return stripHtml(description);
}

/**
 * Whole-phrase match. Short tokens (Go, IT, R) require capitalized forms;
 * everything else uses normalized lowercase haystack.
 * @param {string} haystack - original or already-normalized text
 * @param {string} phrase
 */
function textHasPhrase(haystack, phrase) {
  return textHasKeywordPhrase(haystack, phrase);
}

function uniquePhrases(values) {
  const seen = new Set();
  const out = [];
  for (const raw of values || []) {
    const phrase = String(raw || "").trim();
    if (phrase.length < 2) continue;
    const key = normalizePattern(phrase);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(phrase);
  }
  return out;
}

/** Tokens too generic to prove a related dictionary surface alone. */
const WEAK_SURFACE_TOKENS = new Set([
  "skills",
  "skill",
  "and",
  "the",
  "a",
  "an",
  "of",
  "for",
  "to",
  "in",
  "on",
  "with",
]);

/**
 * True when `pattern` is a valid surface for `canonical`:
 * - exact / contains canonical, or
 * - contains every non-weak canonical token (presentation skills ↔ presentation materials;
 *   financial analysis ✕ investment analysis; financial analysis ✕ financial modeling).
 */
export function patternIsRelatedSurface(pattern, canonical) {
  const p = normalizePattern(pattern);
  const c = normalizePattern(canonical);
  if (!p || !c) return false;
  if (p === c) return true;
  if (c.includes(" ") && p.includes(c)) return true;
  if (p.includes(" ") && c.includes(p)) return true;

  const required = c.split(/\s+/).filter((t) => t.length > 2 && !WEAK_SURFACE_TOKENS.has(t));
  if (!required.length) return false;
  return required.every((t) => {
    // Allow simple plurals: presentation ↔ presentations
    const re = new RegExp(`(?<!\\w)${escapeRegExp(t)}s?(?!\\w)`, "i");
    return re.test(p);
  });
}

function sameConcept(a, b) {
  const left = normalizePattern(a?.keyword_normalized || a?.keyword || "");
  const right = normalizePattern(b?.keyword_normalized || b?.keyword || "");
  return Boolean(left && right && left === right);
}

/** Extra JD surfaces when matched_pattern was not stored on older keyword rows. */
const EXTRA_JD_SURFACES = {
  "user experience": ["UX", "user-experience", "User Experience"],
  "frontend development": ["front-end", "Front-End", "frontend", "front end", "Frontend"],
  "front-end development": ["front-end", "Front-End", "frontend"],
  "software engineer": ["Software Engineer", "software engineer"],
  "software engineering": ["software engineering", "Software engineering"],
  react: ["React", "React.js", "ReactJS", "react.js", "reactjs"],
};

/**
 * Valid surface phrases for one concept from job + same-concept resume_match.
 */
function conceptSurfaces(item) {
  const canonical = item?.keyword || item?.keyword_normalized || "";
  const patterns = [canonical];
  // Always trust extractor matched_pattern (how the keyword was found in the JD),
  // even when it is an abbreviation (UX, front-end) that fails token-overlap checks.
  if (item?.matched_pattern) patterns.push(item.matched_pattern);
  if (sameConcept(item, item?.resume_match)) {
    patterns.push(item.resume_match?.keyword);
    const resumePattern = item.resume_match?.matched_pattern;
    // Resume matched_pattern may be a stale/wrong alias (investment analysis ≠ FA).
    if (resumePattern && patternIsRelatedSurface(resumePattern, canonical)) {
      patterns.push(resumePattern);
    }
  }
  const normKey = normalizePattern(item?.keyword_normalized || item?.keyword || "");
  // Dictionary aliases (MS Office ↔ Microsoft Office, UX ↔ User Experience, …).
  for (const surface of dictionarySurfacesFor(normKey)) {
    patterns.push(surface);
  }
  for (const extra of EXTRA_JD_SURFACES[normKey] || []) {
    patterns.push(extra);
  }
  return uniquePhrases(patterns);
}

/**
 * True when resume prose contains a valid surface form of this job keyword.
 * @param {object} jobItem
 * @param {string} resumeText
 * @param {object | null | undefined} [resumeMatch]
 */
export function resumeHasExactKeywordEvidence(jobItem, resumeText, resumeMatch = null) {
  if (!normalizeMatchText(resumeText)) return false;
  const phrases = conceptSurfaces({
    ...jobItem,
    resume_match: resumeMatch || null,
  });
  return phrases.some((phrase) => textHasPhrase(resumeText, phrase));
}

/**
 * Evidence phrases for UI: identical shared string, or cross-surface proof
 * (JD has one valid surface, resume has another for the same concept).
 */
export function findSharedPhrases(jobText, resumeText, item) {
  if (!normalizeMatchText(jobText) || !normalizeMatchText(resumeText)) return [];

  const surfaces = conceptSurfaces(item);
  const identical = surfaces
    .filter((phrase) => textHasPhrase(jobText, phrase) && textHasPhrase(resumeText, phrase))
    .sort((a, b) => b.length - a.length);
  if (identical.length) return identical;

  const onJob = surfaces.filter((phrase) => textHasPhrase(jobText, phrase));
  const onResume = surfaces.filter((phrase) => textHasPhrase(resumeText, phrase));
  if (!onJob.length || !onResume.length) return [];

  // Prefer canonical when present on the resume; else longest resume surface.
  const canonical = String(item?.keyword || "").trim();
  const preferred =
    onResume.find((p) => normalizePattern(p) === normalizePattern(canonical)) ||
    [...onResume].sort((a, b) => b.length - a.length)[0];
  return preferred ? [preferred] : [];
}

/**
 * Keep only concept matches that also have exact / related-surface evidence.
 * @param {Array} matchedConceptItems - items from computeMatchScore().matched
 * @param {string} jobText
 * @param {string} resumeText
 */
export function filterExactMatches(matchedConceptItems, jobText, resumeText) {
  const result = [];
  for (const item of matchedConceptItems || []) {
    const shared = findSharedPhrases(jobText, resumeText, item);
    if (!shared.length) continue;
    result.push({
      ...item,
      shared_phrases: shared,
      highlight_phrase: shared[0],
    });
  }
  return result;
}

/**
 * Build highlight list: same phrase used on both sides.
 */
export function exactMatchHighlights(exactMatches) {
  const seen = new Set();
  const out = [];
  for (const item of exactMatches || []) {
    for (const phrase of item.shared_phrases || [item.highlight_phrase]) {
      const key = String(phrase || "")
        .trim()
        .toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({ phrase: String(phrase).trim(), tone: "match" });
    }
  }
  return out.sort((a, b) => b.phrase.length - a.phrase.length);
}

/**
 * Prefer canonical surface when present, else longest hit.
 * @param {string[]} hits
 * @param {object} item
 */
function rankSurfaceHits(hits, item) {
  const canonical = String(item?.keyword || "").trim();
  const canonicalNorm = normalizePattern(canonical);
  return [...hits].sort((a, b) => {
    const aCanon = normalizePattern(a) === canonicalNorm;
    const bCanon = normalizePattern(b) === canonicalNorm;
    // Prefer exact canonical over longer dictionary padding ("software engineering"
    // over "software engineering principles").
    if (aCanon !== bCanon) return aCanon ? -1 : 1;
    // Same tier: prefer longer surface ("Redux Toolkit" over bare "Redux").
    if (a.length !== b.length) return b.length - a.length;
    return a.localeCompare(b);
  });
}

/**
 * Surface phrases of a keyword that appear in the job description.
 * Prefers the canonical label when it appears (so we highlight
 * "software engineering" rather than "software engineering principles").
 * @param {string} jobText
 * @param {object} item
 * @returns {string[]}
 */
export function findJobSurfacePhrases(jobText, item) {
  if (!normalizeMatchText(jobText)) return [];
  const surfaces = conceptSurfaces(item);
  const hits = surfaces.filter((phrase) => textHasPhrase(jobText, phrase));
  if (!hits.length) return [];
  return rankSurfaceHits(hits, item);
}

/**
 * Surface phrases of a keyword that appear in resume prose / chips text.
 * Uses dictionary aliases so JD "MS Office" still marks resume "Microsoft Office".
 * @param {string} resumeText
 * @param {object} item
 * @returns {string[]}
 */
export function findResumeSurfacePhrases(resumeText, item) {
  if (!normalizeMatchText(resumeText)) return [];
  const surfaces = conceptSurfaces(item);
  const hits = surfaces.filter((phrase) => textHasPhrase(resumeText, phrase));
  if (!hits.length) return [];
  return rankSurfaceHits(hits, item);
}

/**
 * Keep only job keywords that have a real whole-phrase surface in the JD.
 * Drops phantom extractions like Sales and Trading via S&T → "s and t".
 * @param {Array} jobKeywords
 * @param {string} jobText
 */
export function filterKeywordsPresentInJob(jobKeywords, jobText) {
  return (jobKeywords || []).filter((item) => findJobSurfacePhrases(jobText, item).length > 0);
}

/**
 * Number keywords by first appearance in the JD (book-footnote style).
 * Matched → green; missed → red. Keywords absent from JD keep a number but no mark.
 *
 * @param {Array} matchedItems
 * @param {Array} missedItems
 * @param {string} jobText
 * @returns {Array<{
 *   item: object,
 *   footnote: number,
 *   tone: 'match' | 'gap',
 *   jd_phrase: string,
 *   label: string,
 * }>}
 */
export function buildKeywordFootnotes(matchedItems, missedItems, jobText) {
  const rawJob = String(jobText || "");
  const lowerJob = rawJob.toLowerCase();

  /** @type {Array<{ item: object, tone: 'match' | 'gap', jd_phrase: string, label: string, index: number }>} */
  const entries = [];

  const pushEntry = (item, tone) => {
    if (!item) return;
    const surfaces = findJobSurfacePhrases(rawJob, item);
    const jd_phrase = String(
      surfaces[0] || item.highlight_phrase || item.keyword || item.keyword_normalized || "",
    ).trim();
    const label = String(item.keyword || item.highlight_phrase || item.keyword_normalized || "").trim();
    let index = Number.MAX_SAFE_INTEGER;
    if (jd_phrase && surfaces.length) {
      const at = lowerJob.indexOf(jd_phrase.toLowerCase());
      if (at >= 0) index = at;
    }
    entries.push({ item, tone, jd_phrase, label: label || jd_phrase, index });
  };

  for (const item of matchedItems || []) pushEntry(item, "match");
  for (const item of missedItems || []) pushEntry(item, "gap");

  entries.sort((a, b) => {
    if (a.index !== b.index) return a.index - b.index;
    return String(a.label).localeCompare(String(b.label));
  });

  return entries.map((entry, i) => ({
    ...entry.item,
    footnote: i + 1,
    tone: entry.tone,
    jd_phrase: entry.jd_phrase,
    label: entry.label,
    contributionPercent: entry.item.contributionPercent,
  }));
}

/**
 * Highlights for JD text with footnote numbers.
 * @param {ReturnType<typeof buildKeywordFootnotes>} footnotes
 */
export function footnoteHighlights(footnotes) {
  const seen = new Set();
  const out = [];
  for (const item of footnotes || []) {
    const phrase = String(item?.jd_phrase || "").trim();
    if (phrase.length < 2) continue;
    const key = phrase.toLowerCase();
    // Prefer first (earlier footnote / longer already sorted by caller via phrase length later)
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      phrase,
      tone: item.tone === "gap" ? "gap" : "match",
      note: Number(item.footnote) || null,
    });
  }
  return out.sort((a, b) => b.phrase.length - a.phrase.length);
}

/**
 * Highlights for resume prose with the same footnote numbers as Keyword match chips.
 * Uses resume-side surfaces (aliases) so chips and left-column marks stay in sync.
 *
 * @param {ReturnType<typeof buildKeywordFootnotes>} footnotes
 * @param {string} resumeText
 */
export function footnoteResumeHighlights(footnotes, resumeText) {
  const seen = new Set();
  const out = [];
  for (const item of footnotes || []) {
    if (item?.tone !== "match") continue;
    const surfaces = findResumeSurfacePhrases(resumeText, item);
    const phrase = String(
      surfaces[0] || item.highlight_phrase || item.keyword || item.keyword_normalized || "",
    ).trim();
    if (phrase.length < 2 || !surfaces.length) continue;
    const key = phrase.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      phrase,
      tone: "match",
      note: Number(item.footnote) || null,
    });
  }
  return out.sort((a, b) => b.phrase.length - a.phrase.length);
}

/**
 * All lowercase surface forms for matched footnotes (chips, alias labels).
 * @param {ReturnType<typeof buildKeywordFootnotes>} footnotes
 * @param {string} resumeText
 * @returns {Map<string, { note: number | null }>}
 */
export function matchedResumeSurfaceMeta(footnotes, resumeText) {
  /** @type {Map<string, { note: number | null }>} */
  const map = new Map();
  const put = (raw, note) => {
    const key = String(raw || "")
      .trim()
      .toLowerCase();
    if (key.length < 2 || map.has(key)) return;
    map.set(key, { note: note == null ? null : Number(note) || null });
  };

  for (const item of footnotes || []) {
    if (item?.tone !== "match") continue;
    const note = Number(item.footnote) || null;
    put(item.keyword, note);
    put(item.keyword_normalized, note);
    put(item.label, note);
    put(item.highlight_phrase, note);
    for (const surface of findResumeSurfacePhrases(resumeText, item)) put(surface, note);
    for (const surface of conceptSurfaces(item)) put(surface, note);
  }
  return map;
}
